import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Warning, Trash, CircleNotch, ShieldCheck, Database, Archive } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetDataModal({ isOpen, onClose, companyName }) {
  const [activeTab, setActiveTab] = useState('full');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [step, setStep] = useState(1); // 1: preview, 2: confirm
  
  // Form states
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [createBackup, setCreateBackup] = useState(true);
  
  // Selective reset options
  const [resetSales, setResetSales] = useState(false);
  const [resetPurchases, setResetPurchases] = useState(false);
  const [resetContacts, setResetContacts] = useState(false);
  const [resetAccounting, setResetAccounting] = useState(false);
  const [resetInventory, setResetInventory] = useState(false);
  const [resetHrPayroll, setResetHrPayroll] = useState(false);
  const [resetAuditLogs, setResetAuditLogs] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPreview();
      setStep(1);
      setPassword('');
      setConfirmationText('');
    }
  }, [isOpen]);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/company/reset/preview`);
      setPreview(res.data);
    } catch (error) {
      toast.error('Failed to load data preview');
    } finally {
      setLoading(false);
    }
  };

  const handleFullReset = async () => {
    if (!password || !confirmationText) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setResetting(true);
    try {
      const res = await axios.post(`${API_URL}/api/company/reset/full`, {
        password,
        confirmation_text: confirmationText,
        create_backup: createBackup
      });
      
      toast.success(`Reset complete! ${res.data.total_deleted} records deleted.`);
      onClose();
      // Reload the page to reflect changes
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleSelectiveReset = async () => {
    if (!password || !confirmationText) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const hasSelection = resetSales || resetPurchases || resetContacts || 
                         resetAccounting || resetInventory || resetHrPayroll || resetAuditLogs;
    if (!hasSelection) {
      toast.error('Please select at least one category to reset');
      return;
    }
    
    setResetting(true);
    try {
      const res = await axios.post(`${API_URL}/api/company/reset/selective`, {
        password,
        confirmation_text: confirmationText,
        create_backup: createBackup,
        reset_sales: resetSales,
        reset_purchases: resetPurchases,
        reset_contacts: resetContacts,
        reset_accounting: resetAccounting,
        reset_inventory: resetInventory,
        reset_hr_payroll: resetHrPayroll,
        reset_audit_logs: resetAuditLogs
      });
      
      toast.success(`Reset complete! ${res.data.total_deleted} records deleted.`);
      onClose();
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const resetForm = () => {
    setPassword('');
    setConfirmationText('');
    setStep(1);
    setResetSales(false);
    setResetPurchases(false);
    setResetContacts(false);
    setResetAccounting(false);
    setResetInventory(false);
    setResetHrPayroll(false);
    setResetAuditLogs(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { resetForm(); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Warning size={24} weight="fill" />
            Danger Zone - Reset Data
          </DialogTitle>
          <DialogDescription>
            This action will permanently delete data from your company. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <CircleNotch size={32} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="full" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
                <Trash size={16} className="mr-2" />
                Full Reset
              </TabsTrigger>
              <TabsTrigger value="selective" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700">
                <Database size={16} className="mr-2" />
                Selective Reset
              </TabsTrigger>
            </TabsList>

            {/* Full Reset Tab */}
            <TabsContent value="full" className="space-y-4 mt-4">
              {step === 1 ? (
                <>
                  <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 dark:text-red-400 mb-2">
                      All of the following will be PERMANENTLY DELETED:
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span>Sales Documents:</span>
                        <span className="font-mono font-bold">{preview?.sales_documents || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Purchase Documents:</span>
                        <span className="font-mono font-bold">{preview?.purchase_documents || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Contacts:</span>
                        <span className="font-mono font-bold">{preview?.contacts || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Journal Entries:</span>
                        <span className="font-mono font-bold">{preview?.journal_entries || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Vouchers:</span>
                        <span className="font-mono font-bold">{preview?.vouchers || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Inventory Items:</span>
                        <span className="font-mono font-bold">{preview?.inventory_items || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Employees:</span>
                        <span className="font-mono font-bold">{preview?.employees || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payroll Runs:</span>
                        <span className="font-mono font-bold">{preview?.payroll_runs || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Attendance Records:</span>
                        <span className="font-mono font-bold">{preview?.attendance_records || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Audit Logs:</span>
                        <span className="font-mono font-bold">{preview?.audit_logs || 0}</span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800 flex justify-between font-bold text-red-700 dark:text-red-400">
                      <span>TOTAL RECORDS:</span>
                      <span className="font-mono">{preview?.total_records || 0}</span>
                    </div>
                  </div>

                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
                      <ShieldCheck size={18} />
                      The following will be PRESERVED:
                    </h4>
                    <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                      <li>• Company settings (name, logo, tax config, GST details)</li>
                      <li>• User accounts and roles</li>
                      <li>• Chart of Accounts structure (balances reset to 0)</li>
                      <li>• Subscription and billing information</li>
                    </ul>
                  </div>

                  <Button 
                    onClick={() => setStep(2)} 
                    variant="destructive" 
                    className="w-full"
                    disabled={preview?.total_records === 0}
                  >
                    {preview?.total_records === 0 ? 'No Data to Reset' : 'Continue to Confirmation'}
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-amber-100 dark:bg-amber-950/30 rounded-lg">
                      <Archive size={20} className="text-amber-700" />
                      <div className="flex-1">
                        <Label className="text-amber-800 dark:text-amber-400">Create backup before reset</Label>
                        <p className="text-xs text-amber-600">Recommended for safety</p>
                      </div>
                      <Switch checked={createBackup} onCheckedChange={setCreateBackup} />
                    </div>

                    <div className="space-y-2">
                      <Label>Enter your password to confirm</Label>
                      <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Your current password"
                        data-testid="reset-password"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Type <span className="font-bold text-red-600">RESET</span> to confirm</Label>
                      <Input
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder="Type RESET"
                        className="font-mono"
                        data-testid="reset-confirmation"
                      />
                    </div>
                  </div>

                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleFullReset}
                      disabled={resetting || confirmationText.toUpperCase() !== 'RESET' || !password}
                      data-testid="confirm-full-reset"
                    >
                      {resetting ? (
                        <>
                          <CircleNotch size={16} className="animate-spin mr-2" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <Trash size={16} className="mr-2" />
                          Delete All Data
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </TabsContent>

            {/* Selective Reset Tab */}
            <TabsContent value="selective" className="space-y-4 mt-4">
              {step === 1 ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Select which categories of data you want to delete:
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-sales" 
                          checked={resetSales} 
                          onCheckedChange={setResetSales}
                          data-testid="reset-sales-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-sales" className="cursor-pointer">Sales Documents</Label>
                          <p className="text-xs text-muted-foreground">Invoices, Quotations, Receipts</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{preview?.sales_documents || 0}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-purchases" 
                          checked={resetPurchases} 
                          onCheckedChange={setResetPurchases}
                          data-testid="reset-purchases-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-purchases" className="cursor-pointer">Purchase Documents</Label>
                          <p className="text-xs text-muted-foreground">Purchase Orders, RFQs, GRNs</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{preview?.purchase_documents || 0}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-contacts" 
                          checked={resetContacts} 
                          onCheckedChange={setResetContacts}
                          data-testid="reset-contacts-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-contacts" className="cursor-pointer">Contacts</Label>
                          <p className="text-xs text-muted-foreground">Customers, Suppliers</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{preview?.contacts || 0}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-accounting" 
                          checked={resetAccounting} 
                          onCheckedChange={setResetAccounting}
                          data-testid="reset-accounting-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-accounting" className="cursor-pointer">Accounting</Label>
                          <p className="text-xs text-muted-foreground">Journal Entries, Vouchers, Bank Transactions</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{(preview?.journal_entries || 0) + (preview?.vouchers || 0)}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-inventory" 
                          checked={resetInventory} 
                          onCheckedChange={setResetInventory}
                          data-testid="reset-inventory-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-inventory" className="cursor-pointer">Inventory</Label>
                          <p className="text-xs text-muted-foreground">Items, Stock Movements</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{(preview?.inventory_items || 0) + (preview?.stock_movements || 0)}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-hr" 
                          checked={resetHrPayroll} 
                          onCheckedChange={setResetHrPayroll}
                          data-testid="reset-hr-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-hr" className="cursor-pointer">HR & Payroll</Label>
                          <p className="text-xs text-muted-foreground">Employees, Payroll Runs, Attendance, Leaves</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">
                        {(preview?.employees || 0) + (preview?.payroll_runs || 0) + (preview?.attendance_records || 0) + (preview?.leave_requests || 0)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          id="reset-audit" 
                          checked={resetAuditLogs} 
                          onCheckedChange={setResetAuditLogs}
                          data-testid="reset-audit-checkbox"
                        />
                        <div>
                          <Label htmlFor="reset-audit" className="cursor-pointer">Audit Logs</Label>
                          <p className="text-xs text-muted-foreground">Activity history</p>
                        </div>
                      </div>
                      <span className="font-mono text-sm">{preview?.audit_logs || 0}</span>
                    </div>
                  </div>

                  <Button 
                    onClick={() => setStep(2)} 
                    variant="destructive" 
                    className="w-full"
                    disabled={!(resetSales || resetPurchases || resetContacts || resetAccounting || resetInventory || resetHrPayroll || resetAuditLogs)}
                  >
                    Continue to Confirmation
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
                      <p className="text-sm text-amber-800 dark:text-amber-400 font-medium">You are about to delete:</p>
                      <ul className="text-sm text-amber-700 dark:text-amber-500 mt-2 space-y-1">
                        {resetSales && <li>• Sales Documents ({preview?.sales_documents || 0})</li>}
                        {resetPurchases && <li>• Purchase Documents ({preview?.purchase_documents || 0})</li>}
                        {resetContacts && <li>• Contacts ({preview?.contacts || 0})</li>}
                        {resetAccounting && <li>• Accounting Entries ({(preview?.journal_entries || 0) + (preview?.vouchers || 0)})</li>}
                        {resetInventory && <li>• Inventory ({(preview?.inventory_items || 0) + (preview?.stock_movements || 0)})</li>}
                        {resetHrPayroll && <li>• HR & Payroll Data</li>}
                        {resetAuditLogs && <li>• Audit Logs ({preview?.audit_logs || 0})</li>}
                      </ul>
                    </div>

                    <div className="flex items-center gap-2 p-3 bg-amber-100 dark:bg-amber-950/30 rounded-lg">
                      <Archive size={20} className="text-amber-700" />
                      <div className="flex-1">
                        <Label className="text-amber-800 dark:text-amber-400">Create backup before reset</Label>
                        <p className="text-xs text-amber-600">Recommended for safety</p>
                      </div>
                      <Switch checked={createBackup} onCheckedChange={setCreateBackup} />
                    </div>

                    <div className="space-y-2">
                      <Label>Enter your password to confirm</Label>
                      <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Your current password"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Type <span className="font-bold text-red-600">RESET</span> to confirm</Label>
                      <Input
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder="Type RESET"
                        className="font-mono"
                      />
                    </div>
                  </div>

                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleSelectiveReset}
                      disabled={resetting || confirmationText.toUpperCase() !== 'RESET' || !password}
                      data-testid="confirm-selective-reset"
                    >
                      {resetting ? (
                        <>
                          <CircleNotch size={16} className="animate-spin mr-2" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <Trash size={16} className="mr-2" />
                          Delete Selected Data
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
